import React from "react";
import type { LucideIcon } from "lucide-react";
import { cn } from "../../lib/utils";

export function PageHeader({ title, subtitle, actions, eyebrow }: { title: string; subtitle?: string; actions?: React.ReactNode; eyebrow?: string }) {
  return (
    <div className="mb-6 flex flex-wrap items-end justify-between gap-3 rise">
      <div>
        {eyebrow && <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">{eyebrow}</p>}
        <h1 className="font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">{title}</h1>
        {subtitle && <p className="mt-1 text-sm text-slate-500">{subtitle}</p>}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </div>
  );
}

export function Kpi({ label, value, icon: Icon, tone = "default", testId, hint, delay = 0 }: { label: string; value: React.ReactNode; icon: LucideIcon; tone?: "default" | "danger" | "warn" | "ok" | "brand"; testId?: string; hint?: string; delay?: number }) {
  const tones = {
    default: "text-slate-700 bg-slate-100",
    danger: "text-rose-700 bg-rose-50",
    warn: "text-amber-800 bg-amber-50",
    ok: "text-emerald-700 bg-emerald-50",
    brand: "text-[color:var(--brand)] bg-indigo-50",
  };
  return (
    <div data-testid={testId} className={cn("card-crisp p-5 rise", `rise-${delay}`)}>
      <div className="flex items-center gap-3">
        <div className={cn("flex h-9 w-9 shrink-0 items-center justify-center rounded-lg", tones[tone])}>
          <Icon className="h-4 w-4" />
        </div>
        <p className="text-sm font-semibold leading-tight text-slate-800">{label}</p>
      </div>
      <p className="mt-4 font-display text-3xl font-bold leading-none tracking-tight text-slate-900">{value}</p>
      <p className="mt-2 text-xs text-slate-500">{hint || "\u00a0"}</p>
    </div>
  );
}

export function Card({ title, children, actions, className, testId, icon: Icon }: { title?: string; children: React.ReactNode; actions?: React.ReactNode; className?: string; testId?: string; icon?: LucideIcon }) {
  return (
    <section data-testid={testId} className={cn("card-crisp", className)}>
      {title && (
        <header className="flex items-center justify-between px-5 pt-4 pb-1">
          <h3 className="flex items-center gap-2.5 font-display text-sm font-semibold text-slate-900">
            {Icon && <span className="flex h-7 w-7 items-center justify-center rounded-md bg-indigo-50 text-[color:var(--brand)]"><Icon className="h-3.5 w-3.5" /></span>}
            {title}
          </h3>
          {actions}
        </header>
      )}
      <div className="p-5">{children}</div>
    </section>
  );
}

export function FilterChip({ active, onClick, children, testId }: { active: boolean; onClick: () => void; children: React.ReactNode; testId?: string }) {
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      className={cn(
        "rounded-full border px-3 py-1 text-xs font-semibold transition-colors",
        active ? "border-[color:var(--brand)] bg-[color:var(--brand)] text-[color:var(--brand-fg)]" : "border-slate-300 bg-white text-slate-600 hover:border-slate-400"
      )}
    >
      {children}
    </button>
  );
}
